import type { DocumentHistory } from '#src/graphql/generated';

import { Box, HStack, Text } from '@chakra-ui/react';
import { diffWords } from 'diff';

/** True when the revision JSON includes one or more `source` entries (e.g. processor). */
export function historyRevisionHasSource(
  historyEntry: DocumentHistory | null | undefined,
): boolean {
  if (!historyEntry) return false;
  const raw = historyEntry.value as { source?: unknown } | null;
  if (!raw || typeof raw !== 'object') return false;
  const s = raw.source;
  return Array.isArray(s) && s.length > 0;
}

/** Extract markdown/body string from a document history GraphQL value payload. */
export function getHistoryEntryBody(
  historyEntry: DocumentHistory | null | undefined,
) {
  if (!historyEntry) return '';
  const historyData = historyEntry.value as
    | {
        value?: { value?: string };
      }
    | string;
  if (
    typeof historyData === 'object' &&
    historyData !== null &&
    'value' in historyData &&
    historyData.value?.value
  ) {
    return historyData.value.value;
  }
  if (typeof historyData === 'string') {
    return historyData;
  }
  return JSON.stringify(historyData, null, 2);
}

interface RevisionWordDiffProps {
  fromValue: string;
  toValue: string;
  fromLabel: string;
  toLabel: string;
}

/** Word-level diff with the same styling as the former standalone compare page. */
export function RevisionWordDiff({
  fromValue,
  toValue,
  fromLabel,
  toLabel,
}: RevisionWordDiffProps) {
  const diff = diffWords(fromValue, toValue);

  return (
    <Box
      border="1px solid"
      borderColor="border"
      borderRadius="md"
      overflow="hidden"
    >
      <Box borderBottom="1px solid" borderColor="border">
        <Box bg="bg.subtle" px={4} py={2}>
          <Text fontSize="sm" fontWeight="semibold" color="fg">
            Diff: {fromLabel} → {toLabel}
          </Text>
        </Box>
        <HStack
          gap={2}
          align="center"
          px={4}
          py={1.5}
          bg="bg.subtle"
          borderTop="1px solid"
          borderColor="border"
        >
          <Text
            as="span"
            fontSize="xs"
            fontWeight="semibold"
            px={1.5}
            py={0.5}
            borderRadius="sm"
            bg="red.100"
            color="red.800"
          >
            NEWER
          </Text>
          <Text
            as="span"
            fontSize="xs"
            fontWeight="semibold"
            px={1.5}
            py={0.5}
            borderRadius="sm"
            bg="green.100"
            color="green.800"
          >
            OLDER
          </Text>
        </HStack>
      </Box>
      <Box
        p={4}
        maxH="400px"
        overflow="auto"
        fontFamily="monospace"
        fontSize="sm"
        bg="bg"
        whiteSpace="pre-wrap"
        lineHeight="1.6"
      >
        {diff.map((part, index) => {
          const color = part.added
            ? 'green.100'
            : part.removed
              ? 'red.100'
              : 'transparent';
          const textColor = part.added
            ? 'green.800'
            : part.removed
              ? 'red.800'
              : 'fg';
          return (
            <Text
              key={index}
              as="span"
              bg={color}
              color={textColor}
              fontWeight={part.added || part.removed ? 'semibold' : 'normal'}
            >
              {part.value}
            </Text>
          );
        })}
      </Box>
    </Box>
  );
}
